/**
 * Level1Scene - 关卡1：速度与位移
 * @description 教学目标：理解速度公式 v=s/t
 * 玩家通过设置初速度使火柴人在指定时间内到达终点
 */

import Phaser from 'phaser';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';

// 关卡1配置
interface Level1SpecificConfig {
  distance: number;
  targetTime: number;
  timeTolerance: number;
  formula: string;
  hint: string;
}

// 游戏状态
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

/**
 * Level1Scene 类
 * 关卡1场景，实现速度公式教学
 */
export class Level1Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level1SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;
  private playerVelocity: number = 0;

  // 游戏对象
  private player: Phaser.GameObjects.Graphics | null = null;
  private playerX: number = 100; // 火柴人当前位置
  private startLine: Phaser.GameObjects.Graphics | null = null;
  private goalZone: Phaser.GameObjects.Graphics | null = null;

  // UI 元素
  private hintText: Phaser.GameObjects.Text | null = null;
  private velocityInput: Phaser.GameObjects.Text | null = null;
  private timerText: Phaser.GameObjects.Text | null = null;
  private startButton: Phaser.GameObjects.Graphics | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;
  private velocityValue: number = 0; // 玩家输入的速度值

  // 路径轨迹
  private pathLine: Phaser.GameObjects.Graphics | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  constructor() {
    super({ key: 'Level1Scene' });
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-1', 'src/levels/levels/level-1.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-1') as LevelConfig;
    
    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level1SpecificConfig;
    }

    // 创建背景
    this.createBackground();

    // 创建游戏场景
    this.createGameScene();

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running') return;

    // 更新时间
    this.elapsedTime += delta / 1000;
    this.updateTimerDisplay();

    // 恒定速度运动：位置 = 初位置 + 速度 * 时间增量
    const dt = delta / 1000;
    this.playerX += this.playerVelocity * dt;

    // 更新火柴人位置
    this.updatePlayerPosition();

    // 绘制路径轨迹
    this.drawPath();

    // 检测胜利/失败
    this.checkGameEnd();
  }

  /**
   * 创建背景
   */
  private createBackground(): void {
    // 深色背景
    this.cameras.main.setBackgroundColor('#1a1a2e');

    // 添加网格线作为参考
    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x2a2a4a, 0.3);

    // 垂直网格线
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(x, 0);
      gridGraphics.lineTo(x, GAME_HEIGHT);
      gridGraphics.strokePath();
    }

    // 水平网格线
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(0, y);
      gridGraphics.lineTo(GAME_WIDTH, y);
      gridGraphics.strokePath();
    }
  }

  /**
   * 创建游戏场景
   */
  private createGameScene(): void {
    const y = GAME_HEIGHT / 2;

    // 起点线
    this.startLine = this.add.graphics();
    this.startLine.lineStyle(3, 0xff6b6b);
    this.startLine.beginPath();
    this.startLine.moveTo(100, y - 100);
    this.startLine.lineTo(100, y + 100);
    this.startLine.strokePath();

    // 起点标签
    this.add.text(100, y + 120, '起点', {
      fontSize: '16px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 终点区域（目标）
    const goalX = 100 + (this.levelSpecific?.distance ?? 400);
    this.goalZone = this.add.graphics();
    this.goalZone.fillStyle(0x4ade80, 0.3);
    this.goalZone.fillRect(goalX - 30, y - 50, 60, 100);
    this.goalZone.lineStyle(3, 0x4ade80);
    this.goalZone.strokeRect(goalX - 30, y - 50, 60, 100);

    // 终点标签
    this.add.text(goalX, y + 70, '终点', {
      fontSize: '16px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 距离标注
    this.add.text((100 + goalX) / 2, y - 120, 
      `距离 s = ${this.levelSpecific?.distance ?? 400}`, {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 添加距离箭头
    const arrowGraphics = this.add.graphics();
    arrowGraphics.lineStyle(2, 0x888888);
    arrowGraphics.beginPath();
    arrowGraphics.moveTo(100, y - 110);
    arrowGraphics.lineTo(goalX, y - 110);
    arrowGraphics.moveTo(goalX - 10, y - 115);
    arrowGraphics.lineTo(goalX, y - 110);
    arrowGraphics.lineTo(goalX - 10, y - 105);
    arrowGraphics.strokePath();

    // 创建火柴人（简线条风格）
    this.createPlayer();

    // 路径轨迹（初始化）
    this.pathLine = this.add.graphics();
  }

  /**
   * 创建火柴人
   */
  private createPlayer(): void {
    const y = GAME_HEIGHT / 2;
    this.playerX = 100;

    this.player = this.add.graphics();
    this.drawStickman(this.playerX, y);
  }

  /**
   * 绘制简线条火柴人
   */
  private drawStickman(x: number, y: number): void {
    if (!this.player) return;

    this.player.clear();

    // 白色线条
    this.player.lineStyle(3, 0xffffff);
    this.player.fillStyle(0x1a1a2e);

    // 头部（圆形）
    this.player.fillCircle(x, y - 30, 15);
    this.player.strokeCircle(x, y - 30, 15);

    // 躯干（简线条）
    this.player.beginPath();
    this.player.moveTo(x, y - 15);
    this.player.lineTo(x, y + 20);
    this.player.strokePath();

    // 左臂
    this.player.beginPath();
    this.player.moveTo(x, y - 5);
    this.player.lineTo(x - 20, y - 15);
    this.player.strokePath();

    // 右臂
    this.player.beginPath();
    this.player.moveTo(x, y - 5);
    this.player.lineTo(x + 20, y - 15);
    this.player.strokePath();

    // 左腿
    this.player.beginPath();
    this.player.moveTo(x, y + 20);
    this.player.lineTo(x - 15, y + 50);
    this.player.strokePath();

    // 右腿
    this.player.beginPath();
    this.player.moveTo(x, y + 20);
    this.player.lineTo(x + 15, y + 50);
    this.player.strokePath();

    // 速度箭头（如果正在移动）
    if (this.gameState === 'running' && this.playerVelocity > 0) {
      this.player.lineStyle(2, 0xffff00);
      this.player.beginPath();
      this.player.moveTo(x + 25, y - 30);
      this.player.lineTo(x + 45, y - 30);
      this.player.moveTo(x + 40, y - 35);
      this.player.lineTo(x + 45, y - 30);
      this.player.lineTo(x + 40, y - 25);
      this.player.strokePath();

      // 速度标签
      this.player.fillStyle(0xffff00, 1);
    }
  }

  /**
   * 更新火柴人位置
   */
  private updatePlayerPosition(): void {
    const y = GAME_HEIGHT / 2;
    this.drawStickman(this.playerX, y);
  }

  /**
   * 绘制路径轨迹
   */
  private drawPath(): void {
    if (!this.pathLine) return;

    const y = GAME_HEIGHT / 2;
    this.pathLine.lineStyle(2, 0xffff00, 0.5);
    this.pathLine.beginPath();
    this.pathLine.moveTo(100, y);
    this.pathLine.lineTo(this.playerX, y);
    this.pathLine.strokePath();
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 公式显示
    this.add.text(GAME_WIDTH / 2, 50,
      '速度公式：v = s / t', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(GAME_WIDTH / 2, 85,
      this.levelSpecific?.hint ?? '设置速度使火柴人在指定时间内到达终点', {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 速度输入区域
    this.createVelocityInput();

    // 开始按钮
    this.createStartButton();

    // 计时器显示
    this.timerText = this.add.text(GAME_WIDTH / 2, 520, '时间: 0.00 秒', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 目标时间提示
    const targetTime = this.levelSpecific?.targetTime ?? 5;
    const tolerance = this.levelSpecific?.timeTolerance ?? 0.5;
    this.add.text(GAME_WIDTH / 2, 550, 
      `目标时间: ${targetTime}秒 (允许误差 ±${tolerance}秒)`, {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontSize: '32px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);
  }

  /**
   * 创建速度输入区域
   */
  private createVelocityInput(): void {
    // 输入框背景
    const inputBg = this.add.graphics();
    inputBg.fillStyle(0x2a2a4a, 1);
    inputBg.fillRect(300, 450, 200, 40);
    inputBg.lineStyle(2, 0x4a4a6a);
    inputBg.strokeRect(300, 450, 200, 40);

    // 输入提示
    this.add.text(300, 430, '设置速度 v (单位/秒):', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 显示当前速度值
    this.velocityInput = this.add.text(400, 470, '0', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 增加按钮
    this.createButton(520, 470, '+', () => {
      if (this.gameState === 'idle') {
        this.velocityValue = Math.min(200, this.velocityValue + 10);
        this.updateVelocityDisplay();
      }
    });

    // 减少按钮
    this.createButton(280, 470, '-', () => {
      if (this.gameState === 'idle') {
        this.velocityValue = Math.max(0, this.velocityValue - 10);
        this.updateVelocityDisplay();
      }
    });

    // 预设速度按钮
    this.createPresetButtons();
  }

  /**
   * 创建预设速度按钮
   */
  private createPresetButtons(): void {
    const presets = [60, 80, 100];
    const targetTime = this.levelSpecific?.targetTime ?? 5;
    const distance = this.levelSpecific?.distance ?? 400;
    const correctVelocity = distance / targetTime; // 80

    presets.forEach((v, i) => {
      const x = 300 + i * 80;
      const isCorrect = v === correctVelocity;

      const btn = this.add.graphics();
      btn.fillStyle(isCorrect ? 0x4ade80 : 0x4a4a6a, 1);
      btn.fillRect(x - 30, 500, 60, 30);
      btn.lineStyle(1, isCorrect ? 0x4ade80 : 0x6a6a8a);
      btn.strokeRect(x - 30, 500, 60, 30);

      this.add.text(x, 515, `${v}`, {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);

      // 点击事件
      btn.setInteractive(
        new Phaser.Geom.Rectangle(x - 30, 500, 60, 30),
        Phaser.Geom.Rectangle.Contains
      );
      btn.on('pointerdown', () => {
        if (this.gameState === 'idle') {
          this.velocityValue = v;
          this.updateVelocityDisplay();
        }
      });

      // hover 效果
      btn.on('pointerover', () => {
        btn.clear();
        btn.fillStyle(isCorrect ? 0x5aee90 : 0x5a5a7a, 1);
        btn.fillRect(x - 30, 500, 60, 30);
        btn.strokeRect(x - 30, 500, 60, 30);
      });

      btn.on('pointerout', () => {
        btn.clear();
        btn.fillStyle(isCorrect ? 0x4ade80 : 0x4a4a6a, 1);
        btn.fillRect(x - 30, 500, 60, 30);
        btn.strokeRect(x - 30, 500, 60, 30);
      });
    });
  }

  /**
   * 创建通用按钮
   */
  private createButton(x: number, y: number, label: string, callback: () => void): Phaser.GameObjects.Graphics {
    const btn = this.add.graphics();
    btn.fillStyle(0x4a4a6a, 1);
    btn.fillRect(x - 25, y - 15, 50, 30);
    btn.lineStyle(1, 0x6a6a8a);
    btn.strokeRect(x - 25, y - 15, 50, 30);

    this.add.text(x, y, label, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    btn.setInteractive(
      new Phaser.Geom.Rectangle(x - 25, y - 15, 50, 30),
      Phaser.Geom.Rectangle.Contains
    );
    btn.on('pointerdown', callback);

    btn.on('pointerover', () => {
      btn.clear();
      btn.fillStyle(0x5a5a7a, 1);
      btn.fillRect(x - 25, y - 15, 50, 30);
      btn.strokeRect(x - 25, y - 15, 50, 30);
    });

    btn.on('pointerout', () => {
      btn.clear();
      btn.fillStyle(0x4a4a6a, 1);
      btn.fillRect(x - 25, y - 15, 50, 30);
      btn.strokeRect(x - 25, y - 15, 50, 30);
    });

    return btn;
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRect(620, 450, 120, 50);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRect(620, 450, 120, 50);

    this.add.text(680, 475, '开始', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(620, 450, 120, 50),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startGame());

    this.startButton.on('pointerover', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x5aee90, 1);
      this.startButton?.fillRect(620, 450, 120, 50);
      this.startButton?.strokeRect(620, 450, 120, 50);
    });

    this.startButton.on('pointerout', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x4ade80, 1);
      this.startButton?.fillRect(620, 450, 120, 50);
      this.startButton?.strokeRect(620, 450, 120, 50);
    });
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    // 键盘输入（数字键）
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'idle') return;

      if (event.key >= '0' && event.key <= '9') {
        const digit = parseInt(event.key);
        if (this.velocityValue < 100) {
          this.velocityValue = this.velocityValue * 10 + digit;
          this.updateVelocityDisplay();
        }
      }

      if (event.key === 'Backspace') {
        this.velocityValue = Math.floor(this.velocityValue / 10);
        this.updateVelocityDisplay();
      }

      if (event.key === 'Enter') {
        this.startGame();
      }

      if (event.key === 'r' || event.key === 'R') {
        this.resetGame();
      }
    });
  }

  /**
   * 更新速度显示
   */
  private updateVelocityDisplay(): void {
    if (this.velocityInput) {
      this.velocityInput.setText(`${this.velocityValue}`);
    }

    // 显示计算结果
    const distance = this.levelSpecific?.distance ?? 400;
    const expectedTime = this.velocityValue > 0 ? distance / this.velocityValue : Infinity;

    if (this.velocityValue > 0) {
      this.hintText?.setText(
        `设置速度 v=${this.velocityValue} → 预计用时 t=${expectedTime.toFixed(2)}秒`
      );
    } else {
      this.hintText?.setText(this.levelSpecific?.hint ?? '请设置速度');
    }
  }

  /**
   * 更新计时器显示
   */
  private updateTimerDisplay(): void {
    if (this.timerText) {
      this.timerText.setText(`时间: ${this.elapsedTime.toFixed(2)} 秒`);
    }
  }

  /**
   * 开始游戏
   */
  private startGame(): void {
    if (this.gameState !== 'idle') return;
    if (this.velocityValue <= 0) {
      this.hintText?.setText('请先设置速度！');
      return;
    }

    this.gameState = 'running';
    this.elapsedTime = 0;
    this.playerVelocity = this.velocityValue;
    this.playerX = 100;

    // 清除路径轨迹
    if (this.pathLine) {
      this.pathLine.clear();
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 更改按钮状态
    this.updateStartButtonState('running');
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
      this.startButton.fillRect(620, 450, 120, 50);
    } else if (state === 'idle') {
      this.startButton.fillStyle(0x4ade80, 1);
      this.startButton.fillRect(620, 450, 120, 50);
    }
    this.startButton.strokeRect(620, 450, 120, 50);
  }

  /**
   * 检测游戏结束
   */
  private checkGameEnd(): void {
    const goalX = 100 + (this.levelSpecific?.distance ?? 400);
    const targetTime = this.levelSpecific?.targetTime ?? 5;
    const tolerance = this.levelSpecific?.timeTolerance ?? 0.5;

    // 检测是否到达终点
    if (this.playerX >= goalX - 30) {
      // 停止移动
      this.playerVelocity = 0;
      this.gameState = 'success';

      // 检测时间是否在目标范围内
      const timeDiff = Math.abs(this.elapsedTime - targetTime);
      const isWin = timeDiff <= tolerance;

      if (isWin) {
        this.showSuccessResult();
      } else {
        this.showFailResult(`到达终点，但用时 ${this.elapsedTime.toFixed(2)}秒`);
      }

      this.updateStartButtonState('idle');
    }

    // 检测是否超出边界
    if (this.playerX > GAME_WIDTH) {
      this.gameState = 'failed';
      this.showFailResult('超出边界！');
      this.playerVelocity = 0;
      this.updateStartButtonState('idle');
    }
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(1, this.elapsedTime);

    this.resultText?.setText(`🎉 恭喜！用时 ${this.elapsedTime.toFixed(2)}秒`)
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText(`完美！v = s / t = 400 / 5 = 80 单位/秒`);

    this.createCompletionButtons();
  }

  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 80;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(2)) {
        this.scene.start('Level2Scene');
      }
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  /**
   * 显示失败结果
   */
  private showFailResult(reason: string): void {
    const targetTime = this.levelSpecific?.targetTime ?? 5;
    const distance = this.levelSpecific?.distance ?? 400;
    const correctVelocity = distance / targetTime;

    this.resultText?.setText(`❌ ${reason}`)
      .setColor('#ff6b6b')
      .setVisible(true);

    this.hintText?.setText(`正确速度: v = s / t = ${distance} / ${targetTime} = ${correctVelocity} 单位/秒`);
  }

  /**
   * 重置游戏
   */
  private resetGame(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;
    this.playerVelocity = 0;
    this.playerX = 100;

    // 清除路径
    if (this.pathLine) {
      this.pathLine.clear();
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 重置火柴人位置
    this.updatePlayerPosition();

    // 重置计时器
    this.updateTimerDisplay();

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '设置速度使火柴人在指定时间内到达终点');

    // 更新按钮状态
    this.updateStartButtonState('idle');
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-1');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 1,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取当前时间
   */
  getElapsedTime(): number {
    return this.elapsedTime;
  }

  /**
   * 获取当前速度
   */
  getPlayerVelocity(): number {
    return this.playerVelocity;
  }

  /**
   * 获取当前位置
   */
  getPlayerPosition(): number {
    return this.playerX;
  }
}